import { useEffect } from 'react';
import { Title } from 'rizzui/typography';
import { cn } from '../utils/cn';
import Breadcrumb from './breadcrumb';

export type PageHeaderProps = {
    title: string;
    subtitle?: string;
    breadcrumb?: { name: string; href?: string }[];
    className?: string;
    children?: React.ReactNode;
};

export default function PageHeader({ title, subtitle, breadcrumb = [], children, className }: PageHeaderProps) {
    useEffect(() => {
        const previous = document.title;
        const appName = (window as { __V2__?: { appName?: string } }).__V2__?.appName ?? 'Buckler';
        document.title = title ? `${title} — ${appName}` : appName;
        return () => {
            document.title = previous;
        };
    }, [title]);

    return (
        <header className={cn('mb-6 @container xs:-mt-2 lg:mb-7', className)}>
            <div className="flex flex-col @lg:flex-row @lg:items-center @lg:justify-between">
                <div className="min-w-0 flex-1">
                    <Title
                        as="h2"
                        className="mb-2 text-[22px] font-bold text-gray-900 lg:text-2xl dark:text-gray-100"
                    >
                        {title}
                    </Title>
                    {subtitle ? (
                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                    ) : null}
                    <Breadcrumb separator="" separatorVariant="circle" className="flex-wrap">
                        {breadcrumb.map((item) => (
                            <Breadcrumb.Item key={item.name} {...(item.href ? { href: item.href } : {})}>
                                {item.name}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                </div>
                {children ? (
                    <div className="mt-4 flex flex-col items-stretch gap-3 @lg:mt-0 @lg:flex-row @lg:items-center">
                        {children}
                    </div>
                ) : null}
            </div>
        </header>
    );
}
